"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.BufferStorage = void 0;
const constant_1 = require("../config/constant");
const types_1 = require("../types");
const utils_1 = require("../utils");
const ot_1 = require("./ot");
/**
  * used to cache opBuffer and localChangeset in local localStorage
  * Unsaved data can also be recovered locally for poor network conditions/unexpected shutdown
  */
class BufferStorage {
    constructor(resourceId, resourceType, lsStore) {
        this.resourceId = resourceId;
        this.resourceType = resourceType;
        this.lsStore = lsStore;
        // localStorage encapsulates the instance and binds the namespace in advance
        // https://github.com/nbubna/store
        this.opBufferStorage = this.lsStore.namespace(BufferStorage.bufferStorageNamespace);
        this._opBuffer = [];
        this.localPendingChangesetStorage = this.lsStore.namespace(BufferStorage.pendingChangesetsNamespace);
        this.resumeLocalState();
    }
    // The cache has just been created, but it is still queuing operations that have not been sent out
    // After sending, the op in opBuffer will be assembled into localPendingChangeset
    get opBuffer() {
        return this._opBuffer;
    }
    set opBuffer(value) {
        this._opBuffer = value;
        if (value == null || value.length === 0) {
            this.opBufferStorage.remove(this.resourceId);
            return;
        }
        try {
            this.opBufferStorage.set(this.resourceId, value);
        }
        catch (e) {
            console.error(e);
        }
    }
    // has been sent, waiting for ACK changeset
    get localPendingChangeset() {
        return this._localPendingChangeset;
    }
    set localPendingChangeset(value) {
        this._localPendingChangeset = value;
        if (value == null) {
            this.localPendingChangesetStorage.remove(this.resourceId);
            return;
        }
        try {
            this.localPendingChangesetStorage.set(this.resourceId, value);
        }
        catch (e) {
            // When setting localStorage fails, clear the original value.
            this.localPendingChangesetStorage.remove(this.resourceId);
            console.error(e);
        }
    }
    /**
     * @description reads the data of opBuffer and clears the opBuffer,
     * Considering the delay of the network layer, the front end cannot receive the ack in time,
     * here will save a copy of the opsBuffer data in the storage
     * @param {number} revision
     * @returns
     * @memberof Engine
     */
    deOpBuffer(revision) {
        const ops = this.opBuffer;
        // reset opBuffer
        this.opBuffer = [];
        // set localChangeset
        if (!ops.length) {
            this.localPendingChangeset = undefined;
            return;
        }
        this.localPendingChangeset = BufferStorage.ops2Changeset(ops, revision, this.resourceId, this.resourceType);
    }
    static ops2Changeset(ops, revision, resourceId, resourceType) {
        return {
            messageId: (0, utils_1.generateRandomString)(),
            baseRevision: revision,
            resourceId,
            resourceType,
            operations: (0, ot_1.composeOperations)(ops),
        };
    }
    // Clearing the opBuffer will lose unsynchronized data
    clearOpBuffer() {
        this.opBuffer = [];
    }
    /**
     * @description adds the operation to the buffer send queue.
     */
    pushOpBuffer(operation) {
        // To trigger the setter, assign directly instead of push
        const opBuffer = this.opBuffer;
        this.opBuffer = [...opBuffer, operation];
    }
    clearLocalPendingChangeset() {
        // Clear the local temporary changeset
        this.localPendingChangeset = undefined;
        // this.status = EngineStatus.Clear;
        // // Do a new round of sending
        // this.send();
    }
    /**
     * Restore application state from localStorage.
     * Mainly involves two states, localChangeset and opBuffer.
     * These two variables hold the op that has not been confirmed by the server. And keep backups in localStorage in real time.
     * When the page is refreshed or the data table is re-initialized, the values of localChangeset and opBuffer in memory are lost
     * At this point it is time to restore from localStorage.
     */
    resumeLocalState() {
        this.localPendingChangeset = this.compatibleLocalChangeset(this.localPendingChangesetStorage.get(this.resourceId) || undefined);
        this.opBuffer = this.opBufferStorage.get(this.resourceId) || [];
    }
    /**
      * @description A processing of the collaborative data structure for the online cache
      * Only the localChangeset is considered here. The op buffer storage records the op temp structure , which does not need to be compatible.
      * @param changeset
      * Currently 0.6.2 , this compatibility processing can be removed after two or three versions
      * @private
      */
    compatibleLocalChangeset(changeset) {
        if (!changeset) {
            return;
        }
        if (changeset['datasheetId']) {
            // The datasheetId in the attribute can be considered as the old data structure,
            // and the userId still exists in the old structure, which can be discarded
            const newLocalChangeset = {
                baseRevision: changeset.baseRevision,
                resourceId: changeset['datasheetId'],
                resourceType: types_1.ResourceType.Datasheet,
                operations: changeset.operations,
                messageId: changeset.messageId,
            };
            return newLocalChangeset;
        }
        return changeset;
    }
}
exports.BufferStorage = BufferStorage;
BufferStorage.bufferStorageNamespace = `${constant_1.LS_DATASHEET_NAMESPACE}.opBuffer`;
BufferStorage.pendingChangesetsNamespace = `${constant_1.LS_DATASHEET_NAMESPACE}.localChangeset`;
//# sourceMappingURL=buffer_storage.js.map